import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  interviewEndsAt,
  type InterviewCancelReasonCode,
  type InterviewStatus,
  type InterviewView,
  type RecordOutcomeInput,
  type RescheduleInitiator,
} from '@nest-recruit/shared';
import { ApiError, errorMessage } from '../../../lib/api-client';
import { DEFAULT_TIME_ZONE } from '../../../lib/format';
import { useCurrentSession } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { INTERVIEWS_KEY, interviewKey, interviewsApi, schedulerKey } from '../api/interviews-api';

/** Whose clock the screens read: the viewer's own zone (D14 per-user override), else Dhaka. */
export function useViewerZone(): string {
  return useCurrentSession()?.timezone ?? DEFAULT_TIME_ZONE;
}

/** The double-booking warning comes back as a 409 carrying the clashes in words. */
export function warningsOf(error: unknown): readonly string[] | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const warnings: unknown = error.details.warnings;
  return Array.isArray(warnings) && warnings.every((line) => typeof line === 'string') ? warnings : null;
}

export interface BookOptions {
  readonly startsAt: string;
  readonly meetingLink?: string | null;
  readonly roomId?: string | null;
  readonly isConflictAccepted: boolean;
}

/**
 * SCH-01/04/05's actions. Each change shows at once and the server's answer replaces
 * it; a refusal puts the screen back and says why (CLAUDE.md §5a rules 1 and 3). A
 * double-booking comes back as warnings for the screen to show beside the button, not
 * as a failure.
 */
export function useInterviewActions(interviewId: string) {
  const queryClient = useQueryClient();
  const show = useToasts((state) => state.show);
  const key = interviewKey(interviewId);

  const settle = useCallback(async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [...INTERVIEWS_KEY, 'list'] }),
      queryClient.invalidateQueries({ queryKey: [...INTERVIEWS_KEY, 'application'] }),
      queryClient.invalidateQueries({ queryKey: schedulerKey(interviewId) }),
    ]);
  }, [queryClient, interviewId]);

  /** The common shape: optimistic, call, replace or roll back. Returns the view, or the warnings, or null. */
  const run = useCallback(
    async (
      failure: string,
      call: () => Promise<InterviewView>,
      optimistic?: (view: InterviewView) => InterviewView,
    ): Promise<{ readonly view: InterviewView } | { readonly warnings: readonly string[] } | null> => {
      const snapshot = queryClient.getQueryData<InterviewView>(key);
      if (snapshot && optimistic) queryClient.setQueryData(key, optimistic(snapshot));
      try {
        const view = await call();
        queryClient.setQueryData(key, view);
        // §5a rule 3: hold the new state until the lists have caught up.
        await settle();
        return { view };
      } catch (error) {
        queryClient.setQueryData(key, snapshot);
        const warnings = warningsOf(error);
        if (warnings) return { warnings };
        show({ tone: 'danger', message: failure, detail: errorMessage(error) });
        return null;
      }
    },
    [queryClient, key, settle, show],
  );

  const offer = useCallback(
    async (view: InterviewView, startsAt: readonly string[]): Promise<boolean> => {
      const snapshot = queryClient.getQueryData<InterviewView>(key);
      queryClient.setQueryData(key, { ...view, status: 'slots_offered' as const });
      try {
        const result = await interviewsApi.offer(interviewId, view.version, startsAt);
        queryClient.setQueryData(key, result.interview);
        await settle();
        show({
          tone: 'success',
          message: `${result.count} time${result.count === 1 ? '' : 's'} sent to ${view.candidateName}`,
          detail: view.candidateEmail === null ? 'They have no email address — use "Copy message" to send the link by WhatsApp.' : 'They can choose one from the email. Times are not held — the first to choose takes it.',
        });
        return true;
      } catch (error) {
        queryClient.setQueryData(key, snapshot);
        show({ tone: 'danger', message: "The times weren't sent", detail: errorMessage(error) });
        return false;
      }
    },
    [queryClient, key, interviewId, settle, show],
  );

  const book = useCallback(
    async (view: InterviewView, options: BookOptions): Promise<InterviewView | readonly string[] | null> => {
      const endsAt = interviewEndsAt(new Date(options.startsAt), view.durationMinutes).toISOString();
      const outcome = await run("The interview wasn't booked", () => interviewsApi.book(interviewId, view.version, options), (current) => ({
        ...current,
        status: 'scheduled',
        startsAt: options.startsAt,
        endsAt,
      }));
      if (!outcome) return null;
      if ('warnings' in outcome) return outcome.warnings;
      show({ tone: 'success', message: `Booked with ${view.candidateName}`, detail: 'The candidate and the panel have been told.' });
      return outcome.view;
    },
    [run, interviewId, show],
  );

  const reschedule = useCallback(
    async (view: InterviewView, input: { readonly initiator: RescheduleInitiator; readonly panelistUserId: string | null; readonly note: string | null; readonly startsAt: string | null; readonly isConflictAccepted: boolean }): Promise<InterviewView | readonly string[] | null> => {
      const outcome = await run("The interview wasn't moved", () => interviewsApi.reschedule(interviewId, view.version, input), (current) => ({
        ...current,
        status: input.startsAt ? 'scheduled' : 'rescheduled',
        startsAt: input.startsAt,
        endsAt: input.startsAt ? interviewEndsAt(new Date(input.startsAt), current.durationMinutes).toISOString() : null,
        rescheduleCount: current.rescheduleCount + 1,
      }));
      if (!outcome) return null;
      if ('warnings' in outcome) return outcome.warnings;
      show({
        tone: 'success',
        message: input.startsAt ? 'Moved to the new time' : 'The old time is released',
        detail: input.startsAt ? 'The candidate and the panel have been told.' : 'Send new times or book one — the candidate knows a new time is coming.',
      });
      return outcome.view;
    },
    [run, interviewId, show],
  );

  const substitute = useCallback(
    async (view: InterviewView, fromUserId: string, toUserId: string, isConflictAccepted: boolean): Promise<InterviewView | readonly string[] | null> => {
      const outcome = await run("The panelist wasn't swapped", () => interviewsApi.substitute(interviewId, view.version, { fromUserId, toUserId, isConflictAccepted }));
      if (!outcome) return null;
      if ('warnings' in outcome) return outcome.warnings;
      show({ tone: 'success', message: 'Panelist swapped for this interview', detail: "The job's interview plan is unchanged." });
      return outcome.view;
    },
    [run, interviewId, show],
  );

  const cancel = useCallback(
    async (view: InterviewView, reasonCode: InterviewCancelReasonCode, note: string | null): Promise<boolean> => {
      const outcome = await run("The interview wasn't cancelled", () => interviewsApi.cancel(interviewId, view.version, { reasonCode, note }), (current) => ({
        ...current,
        status: 'cancelled',
        cancelReasonCode: reasonCode,
        statusReason: note,
      }));
      if (outcome && 'view' in outcome) show({ tone: 'success', message: 'Interview cancelled', detail: view.startsAt ? 'The candidate and the panel have been told.' : undefined });
      return outcome !== null && 'view' in outcome;
    },
    [run, interviewId, show],
  );

  const recordOutcome = useCallback(
    async (view: InterviewView, input: Omit<RecordOutcomeInput, 'version'>): Promise<void> => {
      const outcome = await run("That wasn't recorded", () => interviewsApi.outcome(interviewId, view.version, input), (current) => ({ ...current, status: input.outcome satisfies InterviewStatus }));
      if (outcome && 'view' in outcome) {
        const words = { completed: 'Marked completed — scorecards are now due', candidate_no_show: 'Recorded: the candidate did not come', panelist_no_show: 'Recorded: a panelist did not come' } as const;
        show({ tone: 'success', message: words[input.outcome] });
      }
    },
    [run, interviewId, show],
  );

  const cantMakeIt = useCallback(
    async (view: InterviewView, note: string | null): Promise<void> => {
      const outcome = await run("That wasn't sent", () => interviewsApi.cantMakeIt(interviewId, view.version, note));
      if (outcome && 'view' in outcome) show({ tone: 'success', message: 'The recruiter has been told', detail: "They'll swap someone in or move the interview." });
    },
    [run, interviewId, show],
  );

  const details = useCallback(
    async (
      view: InterviewView,
      patch: { readonly meetingLink?: string | null; readonly roomId?: string | null },
      // What the screen shows for the change, which the server only sends back with its answer.
      shown: { readonly roomName?: string | null } = {},
    ): Promise<boolean> => {
      const outcome = await run("That wasn't saved", () => interviewsApi.details(interviewId, view.version, patch), (current) => ({ ...current, ...patch, ...shown }));
      return outcome !== null && 'view' in outcome;
    },
    [run, interviewId],
  );

  /** "Copy message" — the words of the candidate's latest email, for WhatsApp. Logged. */
  const copyMessage = useCallback(async (): Promise<void> => {
    try {
      const { text } = await interviewsApi.copyMessage(interviewId);
      await navigator.clipboard.writeText(text);
      show({ tone: 'success', message: 'Message copied', detail: 'Paste it into WhatsApp or SMS.' });
    } catch (error) {
      show({ tone: 'danger', message: "The message wasn't copied", detail: errorMessage(error) });
    }
  }, [interviewId, show]);

  return { offer, book, reschedule, substitute, cancel, recordOutcome, cantMakeIt, details, copyMessage };
}
